import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { WonderPush } from '@awesome-cordova-plugins/wonderpush/ngx';
import { Platform } from '@ionic/angular';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import mixpanel from 'mixpanel-browser';
import {
  map,
  catchError,
  filter,
  BehaviorSubject,
  switchMap,
  combineLatest,
  take,
  EMPTY,
  startWith,
  of,
} from 'rxjs';
import { IBalanceInfo, IUser } from './auth.models';
import { AppConfigService } from '../services/config.service';
import { PushNotificationsService } from '../services/push-notifications.service';
import { environment } from '../../environments/environment';
import { premiumStateFromSubscriptionSnapshot } from './premium-state';
@Injectable({
  providedIn: 'root',
})
export class UserService {
  initialUser: IUser = {
    id: null,
    uid: null,
    email: null,
    username: null,
    discordLink: null,
    discordUserName: null,
    password: null,
    image: null,
    country: null,
    city: null,
    postalCode: null,
    birthday: null,
    galleryVisibility: null,
    autoTranslate: true,
    gender: 'Other',
    googleMapLink: null,
    mermaidExpLvl: null,
    mersonaName: null,
    profilePicture: null,
    isMailingEnabled: true,
    isPersonalPremium: null,
    platform: null,
    address: '',
    affiliateCode: '',
  };
  user: IUser;
  currentUser$ = new BehaviorSubject<IUser>(this.initialUser);
  adGateReady$ = new BehaviorSubject<boolean>(false);
  isPremium: boolean;
  isAdmin: boolean;
  isAppPremium: boolean;
  isPaidPremium: boolean;
  isPersonalPremium: boolean;
  balanceInfo: IBalanceInfo;
  userSubscriptionInfo;
  private notificationSetupCompletedUid: string | null = null;
  private notificationSetupInFlightUid: string | null = null;
  private currentAuthUid: string;
  private premiumSubscriptionUnsubscribe: () => void;
  private premiumSubscriptionPath: string;
  private userInfoSubscription: any;

  constructor(
    private fireStore: AngularFirestore,
    private configService: AppConfigService,
    private wonderPush: WonderPush,
    private platform: Platform,
    private notificationsService: PushNotificationsService
  ) {}

  setUser = async () => {
    getAuth().onAuthStateChanged(async currentUser => {
      if (!currentUser) {
        this.resetPremiumStateForUser(null);
        this.publishPremiumState('auth-signed-out', null);
        return;
      }

      this.resetPremiumStateForUser(currentUser.uid);
      this.logPremiumState('auth-uid', currentUser.uid);
      this.publishAuthenticatedUserShell(currentUser, 'auth-user-shell-published');

      this.startPremiumHydrationForUser(currentUser);
      this.initializeNotificationsForAuthenticatedUser(currentUser).catch(error =>
        console.error('[premium-state] notification init failed', error)
      );
    });
  };

  startPremiumHydrationForUser(currentUser) {
    this.logPremiumState('auth-startup-hydration-subscribe', currentUser.uid);
    this.publishAuthenticatedUserShell(currentUser, 'auth-user-shell-published');
    this.userInfoSubscription = this.getUserByUid(currentUser.uid)
      .pipe(
        switchMap((user: IUser) => {
          if (this.currentAuthUid !== currentUser.uid) {
            return EMPTY;
          }
          this.user = Object.assign({
            ...this.initialUser,
            ...user,
          });
          this.publishPremiumState('auth-user-profile', currentUser.uid);

          return combineLatest([
            this.getSubscriptionInfo(
              currentUser,
              (this.user as any).id,
              'auth-startup-subscription-snapshot'
            ),
            this.listenBalanceInfoDetails(currentUser.uid).pipe(startWith(null)),
            this.configService.getConfig(),
          ]).pipe(
            filter(
              ([subscriptionReady, info, config]) =>
                config !== undefined
            ),
            map(([subscriptionReady, info, config]) => [info, config])
          );
        })
      )
      .subscribe(async ([info, config]) => {
        if (this.currentAuthUid !== currentUser.uid) {
          return;
        }
        this.balanceInfo = (info as any) || null;
        this.isAdmin = this.balanceInfo?.role === 'admin';
        this.setIsUserPremium(config);
        if (!this.adGateReady$.value) {
          this.adGateReady$.next(true);
        }
        this.publishPremiumState('ad-gate-ready', currentUser.uid);
      });
  }

  async initializeNotificationsForAuthenticatedUser(currentUser) {
    const uid = currentUser?.uid;
    if (!uid) {
      return;
    }

    if (
      this.notificationSetupCompletedUid === uid ||
      this.notificationSetupInFlightUid === uid
    ) {
      return;
    }

    this.notificationSetupInFlightUid = uid;

    try {
      console.log('Initializing notifications for user:', uid);
      await this.platform.ready();

      const isSubscribed = this.platform.is('capacitor')
        ? await this.notificationsService.initNativeWonderPush(
            uid,
            this.wonderPush
          )
        : await this.notificationsService.initNotifications(uid);

      if (isSubscribed) {
        this.notificationSetupCompletedUid = uid;
      }

      this.trackNotificationSettingsEnabled(uid);
    } finally {
      if (this.notificationSetupInFlightUid === uid) {
        this.notificationSetupInFlightUid = null;
      }
    }
  }

  getUserByUid = (uid: string) =>
    this.fireStore
      .collection('users', ref => ref.where('uid', '==', uid))
      .snapshotChanges()
      .pipe(
        switchMap(user => {
          const profileSnapshot = user?.[0];
          if (profileSnapshot) {
            const profileData = profileSnapshot.payload.doc.data() as IUser;
            return of(
              Object.assign(
                { id: profileSnapshot.payload.doc.id, uid },
                profileData
              )
            );
          }

          this.logPremiumState('profile-hydration-missing', uid);
          return this.fireStore
            .collection('users')
            .doc(uid)
            .valueChanges()
            .pipe(
              take(1),
              map((profileData: IUser) => {
                if (!profileData) {
                  this.logPremiumState('profile-hydration-missing-direct-doc', uid);
                  return { ...this.initialUser, id: uid, uid };
                }

                return {
                  ...this.initialUser,
                  id: uid,
                  uid,
                  ...profileData,
                };
              })
            );
        }),

        catchError(async e => {
          console.log(`User with uid ${uid} not found or not exist`);
          this.logPremiumState('profile-hydration-missing-error', uid);
          return { ...this.initialUser, id: uid, uid };
        })
      );

  deleteProfile = async () => {
    this.wonderPush.clearAllData();
    const functions = getFunctions();
    const isDeleted = httpsCallable(functions, 'deleteUser');
    const data = await isDeleted();
    if (data) {
      return data;
    } else {
      throw 'Firebase error';
    }
  };

  async getSubscriptionInfo(
    currentUser,
    userDocumentId?: string,
    source = 'subscription-snapshot'
  ) {
    // const decodedToken = await currentUser.getIdTokenResult();
    // const premium = decodedToken.claims.stripeRole === 'premium';
    const authUid = currentUser?.uid;
    const subscriptionDocumentId = userDocumentId || authUid;
    const subscriptionPath = `users/${subscriptionDocumentId}/subscriptions`;

    if (
      this.premiumSubscriptionUnsubscribe &&
      this.premiumSubscriptionPath === subscriptionPath
    ) {
      this.logPremiumState(`${source}-existing-listener`, authUid, subscriptionPath);
      return Promise.resolve();
    }

    if (this.premiumSubscriptionUnsubscribe) {
      this.premiumSubscriptionUnsubscribe();
    }
    this.premiumSubscriptionPath = subscriptionPath;
    this.logPremiumState(`${source}-listener`, authUid, subscriptionPath);

    return new Promise<void>(resolve => {
      let hasFirstSnapshot = false;
      this.premiumSubscriptionUnsubscribe = this.fireStore
        .collection('users')
        .doc(subscriptionDocumentId)
        .collection('subscriptions')
        .ref.where('status', 'in', ['trialing', 'active'])
        .where('role', '==', 'premium')

        .onSnapshot(async snapshot => {
        this.applyPremiumSnapshotForUser(
          authUid,
          snapshot,
          source,
          subscriptionPath
        );
          if (!hasFirstSnapshot) {
            hasFirstSnapshot = true;
            resolve();
          }
      });
    });
  }

  async refreshPremiumSubscriptionForAuthUser(
    authUid: string,
    source = 'checkout-success-subscription-listener'
  ) {
    if (!authUid) {
      return;
    }

    if (this.currentAuthUid !== authUid) {
      this.resetPremiumStateForUser(authUid);
    }

    try {
      const user = await this.getUserByUid(authUid).pipe(take(1)).toPromise();
      const userDocumentId = (user as any)?.id || authUid;
      await this.getSubscriptionInfo({ uid: authUid }, userDocumentId, source);
    } catch (error) {
      console.error('[premium-state] checkout subscription refresh failed', error);
      await this.getSubscriptionInfo({ uid: authUid }, authUid, source);
    }
  }

  // async isChatVerified(uid) {
  //   const userSubscriptionsRef = this.fireStore
  //     .collection('users')
  //     .doc(uid)
  //     .collection('subscriptions', ref =>
  //       ref.where('status', 'in', ['trialing', 'active'])
  //     );

  //   const snapshot = await userSubscriptionsRef.get().toPromise(); // Get data once

  //   const subscriptions =
  //     snapshot?.docs.map(doc => ({
  //       id: doc.id,
  //       ...doc.data(),
  //     })) || [];
  //   return subscriptions.length > 0;
  // }

  listenBalanceInfoDetails(uid) {
    return this.fireStore.collection('info').doc(uid).valueChanges();
  }

  isUserAuth() {
    return getAuth().currentUser;
  }

  resetPremiumStateForUser(uid: string | null) {
    if (this.currentAuthUid === uid) {
      return;
    }

    if (this.premiumSubscriptionUnsubscribe) {
      this.premiumSubscriptionUnsubscribe();
      this.premiumSubscriptionUnsubscribe = null;
    }
    this.premiumSubscriptionPath = null;
    if (this.userInfoSubscription) {
      this.userInfoSubscription.unsubscribe();
      this.userInfoSubscription = null;
    }

    this.currentAuthUid = uid;
    this.adGateReady$.next(false);
    if (!uid || this.notificationSetupCompletedUid !== uid) {
      this.notificationSetupCompletedUid = null;
      this.notificationSetupInFlightUid = null;
    }
    this.isPaidPremium = false;
    this.isPremium = false;
    this.isPersonalPremium = false;
    this.userSubscriptionInfo = false;
    this.balanceInfo = null;
    this.isAdmin = false;
    if (!uid) {
      this.user = { ...this.initialUser };
      this.currentUser$.next({ ...this.initialUser });
    }
  }

  applyPremiumSnapshotForUser(
    uid: string,
    snapshot: any,
    source = 'subscription-snapshot',
    subscriptionPath?: string
  ) {
    if (this.currentAuthUid !== uid) {
      this.logPremiumState(
        'stale-subscription-snapshot-ignored',
        uid,
        subscriptionPath
      );
      return;
    }

    const premiumState = premiumStateFromSubscriptionSnapshot(snapshot, {
      isAppPremium: this.isAppPremium,
      isPersonalPremium: this.isPersonalPremium,
    });
    this.userSubscriptionInfo = premiumState.subscriptionInfo;
    this.isPaidPremium = premiumState.isPaidPremium;
    this.isPremium = premiumState.isPremium;
    this.publishPremiumState(source, uid, subscriptionPath);
  }

  setIsUserPremium = async config => {
    console.log('current config:', config);
    if (config[0]) {
      this.isAppPremium = config[0].isAppPremium;
    }
    this.isPersonalPremium = this.user.isPersonalPremium;
    this.isPremium =
      this.isPersonalPremium || this.isAppPremium || this.isPaidPremium;
    this.publishPremiumState('user-config', this.user?.uid);
  };

  setPearlsAndShellsBalance = (pearls, shells) => {
    this.balanceInfo.balancePearls = pearls;
    this.balanceInfo.balanceShells = shells;
  };

  setPaidPremium = () => {
    this.isPaidPremium = true;
    this.isPremium = true;
    this.publishPremiumState('set-paid-premium', this.currentAuthUid);
  };

  publishPremiumState(
    source: string,
    uid: string | null,
    subscriptionPath?: string
  ) {
    this.logPremiumState(source, uid, subscriptionPath);
    if (this.user?.uid && this.currentAuthUid === this.user.uid) {
      this.currentUser$.next({ ...this.user });
    }
  }

  private publishAuthenticatedUserShell(currentUser, source: string) {
    const uid = currentUser?.uid;
    if (!uid || this.currentAuthUid !== uid || this.user?.uid === uid) {
      return;
    }

    this.user = {
      ...this.initialUser,
      uid,
      email: currentUser?.email || null,
    };
    this.publishPremiumState(source, uid);
  }

  private logPremiumState(
    source: string,
    uid: string | null,
    subscriptionPath?: string
  ) {
    if (environment.production) {
      return;
    }

    console.info('[premium-state]', {
      source,
      uid,
      currentAuthUid: this.currentAuthUid,
      isPaidPremium: !!this.isPaidPremium,
      isPremium: !!this.isPremium,
      isPersonalPremium: !!this.isPersonalPremium,
      isAppPremium: !!this.isAppPremium,
      isAdmin: !!this.isAdmin,
      hasSubscriptionInfo: !!this.userSubscriptionInfo,
      subscriptionPath,
    });
  }

  private trackNotificationSettingsEnabled(uid: string) {
    mixpanel.init(environment.mixpanelToken, { debug: true });
    mixpanel.identify(uid);
    mixpanel.people.set({
      'Notification Settings Changes': 'enabled',
    });
  }
}

